import { useEffect, useState } from "react";
import { errorReporter } from "~/error-handling";
import { presignReadUrls } from "~/pages/appointments/hooks/useAgentChat";

export function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
}

export interface S3ObjectRef {
  s3_bucket: string;
  s3_key: string;
}

/**
 * Short-lived read URLs, keyed by `s3_key`, for one block's images in a
 * single presign-read request. Re-signs every 50 minutes while mounted (the
 * URLs last an hour), and on `retry`; current URLs stay in place until the
 * new ones arrive, so images already shown don't drop back to loading.
 * Reports `accessError` when any image gets no usable URL.
 */
export function usePresignedReadUrls(
  objects: S3ObjectRef[],
  accessError: { message: string; stage: string },
): { urls: Record<string, string>; loading: boolean; retry: () => void } {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  // A stable value for the dependency list: a new array of the same objects
  // on every render must not re-sign, while a retry or refresh (attempt) must.
  const request = JSON.stringify({
    objects: objects.map(({ s3_bucket, s3_key }) => ({ s3_bucket, s3_key })),
    attempt,
  });
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    async function resolve() {
      try {
        const { objects: keys } = JSON.parse(request) as {
          objects: S3ObjectRef[];
        };
        const signed = await presignReadUrls(keys, controller.signal);
        const usable = Object.fromEntries(
          Object.entries(signed).filter(([, url]) => isHttpsUrl(url)),
        );
        if (controller.signal.aborted) return;
        setUrls(usable);
        if (keys.some(({ s3_key }) => !(s3_key in usable)))
          throw new Error("Image read URL missing");
      } catch {
        if (!controller.signal.aborted)
          errorReporter.reportMessage(accessError.message, {
            level: "warning",
            tags: { feature: "indy", stage: accessError.stage },
          });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    void resolve();
    const timer = window.setTimeout(
      () => setAttempt((value) => value + 1),
      50 * 60 * 1000,
    );
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [request, accessError]);

  return { urls, loading, retry: () => setAttempt((value) => value + 1) };
}
